/**
 * assetSearchQuery — parse the content browser's search box into a name query
 * and a tag filter (ENG-1132).
 *
 * `tag:furniture sofa` means "tagged furniture, and named something like sofa".
 * A prefix in the existing box rather than a new control, for two reasons: the
 * box already works with global search and the folder filter, so tags compose
 * with both for free; and `tag:` is the spelling GitHub, Unreal's content
 * browser and every issue tracker already use, so it needs no discovery UI
 * beyond the placeholder.
 *
 * Several `tag:` terms are ANDed — `tag:furniture tag:lod-2` is the asset with
 * both. That matches how Unreal's per-tag checks compose and is the only
 * reading that lets a filter NARROW as you type; OR would widen the result set
 * with every keystroke, which reads as the filter being broken.
 *
 * Pure: no React, no registry. Runs under `node --test`.
 */

import { normalizeTag } from './assetTags.js'

/**
 * Split a raw search string into `{ text, tags, badTags }`.
 *
 * `text` is everything that was not a tag term, joined by single spaces and
 * lowercased for the caller's substring match.
 *
 * `badTags` carries the `tag:` terms that are not usable tag names, so the UI
 * can say "no such tag" instead of silently returning nothing — a filter that
 * quietly matches zero assets is indistinguishable from an empty folder. A
 * bare `tag:` (the moment after typing the colon) is NOT a bad tag: it is a
 * half-typed term and filtering on it would blank the grid mid-keystroke.
 */
export function parseAssetSearch(raw) {
  const tags = []
  const badTags = []
  const words = []
  for (const term of String(raw ?? '').split(/\s+/)) {
    if (!term) continue
    const m = /^tags?:(.*)$/i.exec(term)
    if (!m) { words.push(term); continue }
    const value = m[1]
    if (value === '') continue // still typing
    const t = normalizeTag(value)
    if (t) { if (!tags.includes(t)) tags.push(t) } else badTags.push(value)
  }
  return { text: words.join(' ').toLowerCase(), tags, badTags }
}

/** True when the parsed query asks for nothing — the caller can skip filtering. */
export function isEmptyAssetSearch(parsed) {
  return !parsed.text && parsed.tags.length === 0 && parsed.badTags.length === 0
}
